import React from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { LoaderIcon } from '../../../components/LoaderIcon';
import { TransportOrder } from '../../../domain/transportOrder';
import { TransportOrderFeedItem } from '../../../domain/transportOrdersFeed';
import {
    useBulkChangeTransportOrderStatusRepository,
} from '../../../repository/transport-orders/useBulkChangeTransportOrderStatusRepository';
import { usePermissions } from '../../../components/auth/usePermissions';
import { Permission } from '../../../domain/permissions';
import { statuses } from '../statuses.const';

type StatusActionForSelectedItemsProps = {
    selectedItems: TransportOrderFeedItem[];
    onSuccess: () => void;
};

export const StatusActionForSelectedItems: React.FC<StatusActionForSelectedItemsProps> = ({
    selectedItems,
    onSuccess,
}) => {
    const { hasPermission } = usePermissions();
    const canUpdateStatus = hasPermission(Permission.TRANSPORT_ORDERS_UPDATE_STATUS);
    const [isLoading, setIsLoading] = React.useState(false);

    const { bulkChangeTransportOrderStatus } = useBulkChangeTransportOrderStatusRepository();

    if (selectedItems.length === 0) {
        return null;
    }

    const handleBulkStatusChange = async (newStatus: TransportOrder['status']) => {
        const selectedIds = selectedItems.map(item => item.id);

        if (selectedIds.length === 0) {
            toast.warning('Nie wybrano żadnych zleceń');
            return;
        }

        try {
            setIsLoading(true);
            await bulkChangeTransportOrderStatus({
                transportOrderIds: selectedIds,
                newStatus,
            });

            toast.success(`Pomyślnie potwierdzono ${selectedIds.length} zleceń`);
            onSuccess();
        } catch (error) {
            toast.error('Wystąpił błąd podczas aktualizacji statusów');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const allToLoad = selectedItems.every(item => item.type === 'toLoad');
    const allToUnload = selectedItems.every(item =>
        item.type === 'toUnload' || item.type === 'toUnloadAndNotYetLoaded',
    );

    if (isLoading) {
        return (
            <Button variant="outline-primary" disabled>
                <LoaderIcon animate={true} />
            </Button>
        );
    }

    const statusToChange = allToLoad ? statuses.toLoad : allToUnload ? statuses.toUnload : null; // null should not be possible because in one table i show only items of one type

    if (!statusToChange || !canUpdateStatus) {
        return null;
    }

    return (
        <Button
            variant="outline-primary"
            onClick={() => handleBulkStatusChange(statusToChange.updateToStatus!.status)}
        >
            {statusToChange.label} ({selectedItems.length})
        </Button>
    );
};
